import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ExternalLink, FileText, Download, BookOpen, File, Info, Globe, Clock, AlertTriangle, Lock, Unlock } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Badge } from "@/components/ui/badge";
import { getDocumentType, getEnhancedNoticeTypeDescription, getNoticeSubtypeId } from "@/utils/procedureTypeUtils";
import { getNoticeSubtypeById, getDocumentTypeDescription } from "@/utils/noticeSubtypeData";
import { AcronymTooltip } from "@/components/ui/acronym-tooltip";
import { formatDate } from "@/utils/dateFormatters";
import { getDocumentRestrictionDescription, getSubmissionLanguageDescription } from "@/utils/procurementFieldDescriptions";
import { useTranslation } from "react-i18next";

interface DocumentsCardProps {
  tender: any;
}

export const DocumentsCard: React.FC<DocumentsCardProps> = ({
  tender
}) => {
  const { t, i18n } = useTranslation("tenderDocuments");
  
  // Get submission URL (where tender documents can be accessed)
  const getSubmissionUrl = () => {
    if (tender["submission-url-lot"] && typeof tender["submission-url-lot"] === "string") {
      return tender["submission-url-lot"];
    }
    if (Array.isArray(tender["submission-url-lot"]) && tender["submission-url-lot"].length > 0) {
      return tender["submission-url-lot"][0];
    }
    if (tender["document-url-lot"]) {
      if (typeof tender["document-url-lot"] === "string") {
        return tender["document-url-lot"];
      }
      if (Array.isArray(tender["document-url-lot"]) && tender["document-url-lot"].length > 0) {
        return tender["document-url-lot"][0];
      }
    }
    if (tender["document-url-part"]) {
      if (typeof tender["document-url-part"] === "string") {
        return tender["document-url-part"];
      }
      if (Array.isArray(tender["document-url-part"]) && tender["document-url-part"].length > 0) {
        return tender["document-url-part"][0];
      }
    }
    return null;
  };

  // Get TED URL (original publication)
  const getTedUrl = () => {
    if (tender.links?.html?.ENG) {
      return tender.links.html.ENG;
    }
    if (tender.tender_data?.links?.html?.ENG) {
      return tender.tender_data.links.html.ENG;
    }
    if (tender.ted_url) {
      return tender.ted_url;
    }
    return null;
  };

  // Get submission languages
  const getSubmissionLanguages = () => {
    if (tender["submission-language"]) {
      return tender["submission-language"];
    }
    if (tender["SUB_LG"]) {
      return tender["SUB_LG"];
    }
    if (tender["BT-97-Lot"]) {
      return tender["BT-97-Lot"];
    }
    return null;
  };

  // Get document restriction status
  const getDocumentRestriction = () => {
    if (tender["document-restricted-lot"]) {
      return tender["document-restricted-lot"];
    }
    if (tender["BT-14-Lot"]) {
      return tender["BT-14-Lot"];
    }
    if (tender["document-restricted-part"]) {
      return tender["document-restricted-part"];
    }
    if (tender["BT-14-Part"]) {
      return tender["BT-14-Part"];
    }
    return null;
  };

  // Get document restriction justification
  const getDocumentRestrictionJustification = () => {
    if (tender["document-restricted-justification"]) {
      return tender["document-restricted-justification"];
    }
    if (tender["document-restricted-justification-part"]) {
      return tender["document-restricted-justification-part"];
    }
    return null;
  };

  // Get deadline for receipt of tenders
  const getDeadline = () => {
    if (tender["deadline-receipt-tender-date-lot"]) {
      return tender["deadline-receipt-tender-date-lot"];
    }
    if (tender["DT"]) {
      return tender["DT"];
    }
    if (tender["deadline-receipt-request"]) {
      return tender["deadline-receipt-request"];
    }
    return null;
  };
  
  const submissionUrl = getSubmissionUrl();
  const tedUrl = getTedUrl();
  const submissionLanguages = getSubmissionLanguages();
  const documentRestriction = getDocumentRestriction();
  const documentRestrictionJustification = getDocumentRestrictionJustification();
  const deadline = getDeadline();

  // Get document type and notice information
  const documentTypeId = getDocumentType(tender);
  const documentTypeDesc = documentTypeId ? getDocumentTypeDescription(documentTypeId) : null;
  const noticeSubtypeId = getNoticeSubtypeId(tender);
  const noticeSubtype = noticeSubtypeId ? getNoticeSubtypeById(noticeSubtypeId) : null;
  const enhancedDescription = getEnhancedNoticeTypeDescription(tender);

  // Get formatted submission language description
  const getFormattedSubmissionLanguages = () => {
    if (!submissionLanguages) return null;
    if (Array.isArray(submissionLanguages)) {
      return submissionLanguages.map(lang => getSubmissionLanguageDescription(lang)).join(', ');
    }
    return getSubmissionLanguageDescription(submissionLanguages);
  };
  
  if (!submissionUrl && !tedUrl) {
    return null;
  }
  
  return <Card className="border-green-200 shadow-sm">
      <CardContent className="p-6 space-y-4">
        <div className="flex justify-between items-start">
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-green-600" />
            {t("title")}
          </h2>
          
          {documentTypeId && <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  
                </TooltipTrigger>
                <TooltipContent className="p-3 max-w-xs">
                  <p className="font-medium">{documentTypeDesc}</p>
                  {enhancedDescription && <p className="text-sm text-muted-foreground mt-1">{enhancedDescription}</p>}
                  {noticeSubtype && <p className="text-xs mt-2">Notice Subtype ID: {noticeSubtype.id}</p>}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>}
        </div>
        
        <div className="flex flex-wrap gap-4 items-center pb-4 border-b">
          {deadline && <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="secondary" className="cursor-help">
                    <Clock className="h-4 w-4 mr-1" />
                    <span>{Array.isArray(deadline) ? formatDate(deadline[0]) : formatDate(deadline)}</span>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("deadlineTooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>}
          
          {submissionLanguages && <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant="outline" className="cursor-help">
                    <Globe className="h-4 w-4 mr-1" />
                    <span>{getFormattedSubmissionLanguages()}</span>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t("languagesTooltip")}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>}
          
          {documentRestriction && <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Badge variant={documentRestriction === "restricted-document" ? "destructive" : "outline"} className="cursor-help">
                    {documentRestriction === "restricted-document" ? <Lock className="h-4 w-4 mr-1" /> : <Unlock className="h-4 w-4 mr-1" />}
                    <span>{getDocumentRestrictionDescription(documentRestriction)}</span>
                  </Badge>
                </TooltipTrigger>
                <TooltipContent>
                  {documentRestrictionJustification ? 
                    <p>{t("justification")}: {documentRestrictionJustification}</p> : 
                    <p>{documentRestriction === "restricted-document" ? 
                      t("restrictedAccess") : 
                      t("fullAccess")}
                    </p>
                  }
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>}
        </div>
        
        <div className="space-y-4">
          {submissionUrl && <div>
              <h3 className="font-medium mb-2">{t("accessDocumentsTitle")}</h3>
              <Button variant="default" className="w-full text-left justify-start gap-2 bg-green-600 hover:bg-green-700" onClick={() => window.open(submissionUrl, "_blank")}>
                <Download className="h-4 w-4" />
                <span className="truncate">{t("accessPlatformButton")}</span>
              </Button>
              {documentRestriction === "restricted-document" ? 
                <div className="flex items-center gap-2 mt-2 text-sm text-amber-600">
                  <AlertTriangle className="h-4 w-4" />
                  <p>{t("restrictedNote")}</p>
                </div> : 
                <p className="text-sm text-muted-foreground mt-2">
                  {t("accessDescription")}
                </p>
              }
            </div>}

          {tedUrl && <div>
              <h3 className="font-medium mb-2">{t("tedPublicationTitle")}</h3>
              <Button variant="outline" className="w-full text-left justify-start gap-2" onClick={() => window.open(tedUrl, "_blank")}>
                <ExternalLink className="h-4 w-4" />
                <span className="truncate">{t("viewOriginalButton")}</span>
              </Button>
              <p className="text-sm text-muted-foreground mt-2">
                {t("officialJournalDescription")}
              </p>
            </div>}
        </div>
      </CardContent>
    </Card>;
};
